import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import Layout from "../components/Layout";
import { useLang } from "../contexts/LanguageContext";
import { PROJECT_IMAGES } from "../i18n/translations";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_bola8-construcao/artifacts/s30lht0c_1.png";

const PRIMEWASH_IMG =
  "https://images.unsplash.com/photo-1774031159721-aec9230f38db?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxjbGVhbmluZyUyMGNvbmNyZXRlJTIwcHJlc3N1cmUlMjB3YXNoZXJ8ZW58MHx8fHwxNzc2Nzc5MTE0fDA&ixlib=rb-4.1.0&q=85";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const Eyebrow = ({ children, className = "" }) => (
  <span className={`inline-block uppercase tracking-[0.28em] text-xs text-[#666] ${className}`}>
    {children}
  </span>
);

const HomePage = () => {
  const { t } = useLang();
  const projects = t.projectsData.filter((p) => !p.hidden);

  return (
    <Layout>
      {/* HERO */}
      <section className="relative h-[92vh] min-h-[640px] overflow-hidden -mt-20" data-testid="hero-section">
        <img
          src={HERO_IMG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/35 to-black/70" />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-24">
          <motion.div
            initial="hidden"
            animate="show"
            variants={{ show: { transition: { staggerChildren: 0.15 } } }}
          >
            <motion.span
              variants={fadeUp}
              className="inline-block uppercase tracking-[0.3em] text-xs text-white/70 mb-6"
              data-testid="hero-eyebrow"
            >
              {t.home.eyebrow}
            </motion.span>
            <motion.h1
              variants={fadeUp}
              className="text-white font-light tracking-tight leading-[1.05] text-4xl sm:text-5xl md:text-6xl lg:text-7xl max-w-4xl"
              style={{ fontFamily: "Outfit, sans-serif" }}
              data-testid="hero-title"
            >
              {t.home.heroTitle}
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="mt-6 text-white/80 text-base md:text-lg max-w-2xl leading-relaxed"
              data-testid="hero-subtitle"
            >
              {t.home.heroSubtitle}
            </motion.p>
            <motion.div variants={fadeUp} className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/contactos"
                data-testid="hero-cta-btn"
                className="group inline-flex items-center gap-3 bg-white text-[#1C1C1C] px-8 py-4 text-xs uppercase tracking-[0.2em] hover:bg-[#D4C9B8] transition-all duration-300"
              >
                {t.home.heroCta}
                <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
              </Link>
              <Link
                to="/projetos"
                data-testid="hero-secondary-cta-btn"
                className="group inline-flex items-center gap-3 border border-white/70 text-white px-8 py-4 text-xs uppercase tracking-[0.2em] hover:bg-white hover:text-[#1C1C1C] transition-all duration-300"
              >
                {t.home.heroSecondaryCta}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* POSICIONAMENTO */}
      <section id="posicionamento" className="py-24 md:py-36 border-b border-[#E5E5E5]" data-testid="positioning-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <Eyebrow>{t.home.positioningEyebrow}</Eyebrow>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mt-6 font-light tracking-tight text-2xl sm:text-3xl md:text-4xl leading-[1.3] text-[#1C1C1C]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {t.home.positioning}
          </motion.p>
        </div>
      </section>

      {/* SERVIÇOS */}
      <section className="py-24 md:py-32 bg-[#EBEBE6]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <Eyebrow>{t.home.servicesEyebrow}</Eyebrow>
              <h2
                className="mt-4 font-light tracking-tight text-3xl md:text-5xl max-w-xl"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                {t.home.servicesTitle}
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Construção Chave na Mão */}
            <Link
              to="/sociedade"
              data-testid="service-construction-card"
              className="group relative overflow-hidden bg-white border border-[#E5E5E5] aspect-[4/5] flex flex-col justify-end p-8 md:p-10 hover:shadow-2xl transition-all duration-500"
            >
              <img
                src={HERO_IMG}
                alt=""
                className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-white via-white/75 to-white/10" />
              <div className="relative z-10">
                <span className="uppercase tracking-[0.28em] text-[10px] text-[#666] mb-3 block">01</span>
                <h3 className="text-xl md:text-2xl font-medium text-[#1C1C1C]" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {t.home.service1Title}
                </h3>
                <p className="mt-3 text-[#555] text-sm leading-relaxed">
                  {t.home.service1Desc}
                </p>
                <span className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#1C1C1C]">
                  {t.home.service1Link} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
                </span>
              </div>
            </Link>

            {/* Desenvolvimento de Projetos */}
            <Link
              to="/sociedade"
              data-testid="service-development-card"
              className="group relative overflow-hidden bg-[#EBEBE6] border border-[#D5D1C9] aspect-[4/5] flex flex-col justify-end p-8 md:p-10 hover:shadow-2xl transition-all duration-500"
            >
              <div className="absolute inset-0 opacity-[0.08] pointer-events-none">
                <img src="/logo_bola8.png" alt="" className="w-full h-full object-contain p-8" />
              </div>
              <div className="relative z-10">
                <span className="uppercase tracking-[0.28em] text-[10px] text-[#666] mb-3 block">02</span>
                <h3 className="text-xl md:text-2xl font-medium text-[#1C1C1C]" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {t.home.service2Title}
                </h3>
                <p className="mt-3 text-[#555] text-sm leading-relaxed">
                  {t.home.service2Desc}
                </p>
                <span className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#1C1C1C]">
                  {t.home.service2Link} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
                </span>
              </div>
            </Link>

            {/* PrimeWash */}
            <Link
              to="/primewash"
              data-testid="service-primewash-card"
              className="group relative overflow-hidden bg-[#0A0E17] aspect-[4/5] flex flex-col justify-end p-8 md:p-10 hover:shadow-2xl transition-all duration-500"
            >
              <img
                src={PRIMEWASH_IMG}
                alt=""
                className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E17] via-[#0A0E17]/80 to-[#0A0E17]/30" />
              <div className="relative z-10">
                <span className="uppercase tracking-[0.28em] text-[10px] text-[#0066FF] mb-3 block">03</span>
                <h3 className="text-xl md:text-2xl font-medium text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {t.home.service3Title}
                </h3>
                <p className="mt-3 text-white/70 text-sm leading-relaxed">
                  {t.home.service3Desc}
                </p>
                <span className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#0066FF]">
                  {t.home.service3Link} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* PROJETOS */}
      <section className="py-24 md:py-32" data-testid="projects-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <Eyebrow>{t.home.projectsEyebrow}</Eyebrow>
              <h2
                className="mt-4 font-light tracking-tight text-3xl md:text-5xl max-w-xl"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                {t.home.projectsTitle}
              </h2>
            </div>
            <Link
              to="/projetos"
              data-testid="projects-cta-link"
              className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] border-b border-[#1C1C1C] pb-1 hover:gap-4 transition-all"
            >
              {t.home.projectsCta} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid md:grid-cols-12 gap-6">
            {projects.map((p, idx) => (
              <motion.div
                key={p.slug}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.7, delay: idx * 0.1 }}
                className={`relative overflow-hidden group aspect-[16/9] md:aspect-[4/3] ${
                  projects.length === 1
                    ? "md:col-span-12"
                    : idx === 0
                    ? "md:col-span-7"
                    : "md:col-span-5"
                }`}
                data-testid={`project-card-${p.slug}`}
              >
                <img
                  src={PROJECT_IMAGES[p.slug]?.[0]}
                  alt={p.name}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.2s]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
                  <p className="uppercase tracking-[0.25em] text-[10px] text-white/70">{p.location}</p>
                  <h3 className="mt-2 text-xl md:text-2xl font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {p.name}
                  </h3>
                  <p className="mt-1 text-xs text-white/70">{p.typology} · {p.status}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DIFERENCIAÇÃO */}
      <section className="py-24 md:py-32 bg-[#1C1C1C] text-white" data-testid="differentiation-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16 max-w-3xl">
            <Eyebrow className="text-white/50">{t.home.diffEyebrow}</Eyebrow>
            <h2
              className="mt-4 font-light tracking-tight text-3xl md:text-5xl"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              {t.home.diffTitle}
            </h2>
            <p className="mt-6 text-white/70 text-base md:text-lg leading-relaxed">
              {t.home.diffIntro}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            {[
              [t.home.diff1Title, t.home.diff1Desc, "01"],
              [t.home.diff2Title, t.home.diff2Desc, "02"],
              [t.home.diff3Title, t.home.diff3Desc, "03"],
              [t.home.diff4Title, t.home.diff4Desc, "04"],
            ].map(([title, desc, num]) => (
              <div key={num} className="bg-[#1C1C1C] p-8 md:p-10 hover:bg-[#222] transition-colors duration-500">
                <p className="text-5xl font-light text-white/20" style={{ fontFamily: "Outfit, sans-serif" }}>{num}</p>
                <h3 className="mt-8 text-lg font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>{title}</h3>
                <p className="mt-3 text-sm text-white/60 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 md:py-32 bg-[#D4C9B8]" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2
            className="font-light tracking-tight text-3xl md:text-5xl text-[#1C1C1C]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {t.home.ctaTitle}
          </h2>
          <p className="mt-6 text-[#3a3a3a] max-w-2xl mx-auto">{t.home.ctaSubtitle}</p>
          <Link
            to="/contactos"
            data-testid="final-cta-btn"
            className="mt-10 inline-flex items-center gap-3 bg-[#1C1C1C] text-white px-10 py-5 text-xs uppercase tracking-[0.2em] hover:bg-black transition"
          >
            {t.nav.cta} <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
